import { useTranslation } from "react-i18next";import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import {
  Camera,
  CameraOff,
  SwitchCamera,
  Volume2,
  VolumeX,
  Loader2,
  CheckCircle2,
  XCircle,
  Smartphone } from
'lucide-react';
import { toast } from 'sonner';

export const CameraScanner = ({
  onScan,
  onError,
  autoStart = false,
  showControls = true,
  className = '',
  scannerConfig = {}
}) => {const { t } = useTranslation("common");
  const [isScanning, setIsScanning] = useState(false);
  const [hasCamera, setHasCamera] = useState(true);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastScan, setLastScan] = useState(null);
  const [error, setError] = useState(null);

  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const scanTimeoutRef = useRef(null);

  // Default scanner configuration
  const defaultConfig = {
    fps: 10,
    qrbox: { width: 250, height: 150 },
    aspectRatio: 1.0,
    disableFlip: false,
    ...scannerConfig
  };

  // Play beep sound on successful scan
  const playBeep = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 1800;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (e) {
      console.warn('Could not play beep:', e);
    }
  }, [soundEnabled]);

  // Get available cameras
  useEffect(() => {
    Html5Qrcode.getCameras().
    then((devices) => {
      if (devices && devices.length > 0) {
        setCameras(devices);
        // Prefer back camera for mobile devices
        const backCamera = devices.find((d) =>
        d.label.toLowerCase().includes('back') ||
        d.label.toLowerCase().includes('rear') ||
        d.label.toLowerCase().includes('environment')
        );
        setSelectedCamera(backCamera?.id || devices[0].id);
        setHasCamera(true);
      } else {
        setHasCamera(false);
        setError('No camera found on this device');
      }
    }).
    catch((err) => {
      console.error('Error getting cameras:', err);
      setHasCamera(false);
      setError('Camera access denied or not available');
    });

    return () => {
      // Cleanup on unmount
      if (html5QrCodeRef.current) {
        try {
          html5QrCodeRef.current.stop().catch(() => {});
        } catch (e) {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-start scanner if enabled
  useEffect(() => {
    if (autoStart && hasCamera && selectedCamera && !isScanning) {
      startScanning();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, hasCamera, selectedCamera]);

  const startScanning = async () => {
    if (!selectedCamera || isScanning) return;

    try {
      setError(null);

      if (!html5QrCodeRef.current) {
        html5QrCodeRef.current = new Html5Qrcode('camera-scanner-reader');
      }

      await html5QrCodeRef.current.start(
        selectedCamera,
        defaultConfig,
        (decodedText, decodedResult) => {
          // Debounce repeated scans
          if (scanTimeoutRef.current) return;

          playBeep();
          setLastScan({
            text: decodedText,
            format: decodedResult?.result?.format?.formatName || 'BARCODE',
            timestamp: new Date().toISOString()
          });

          onScan?.(decodedText, decodedResult);

          // Prevent rapid rescans of same code
          scanTimeoutRef.current = setTimeout(() => {
            scanTimeoutRef.current = null;
          }, 1500);
        },
        (errorMessage) => {

          // QR scan failures are normal, don't log every frame
        });

      setIsScanning(true);
      toast.success('Camera Scanner Active', {
        description: 'Point camera at barcode to scan'
      });
    } catch (err) {
      console.error('Error starting scanner:', err);
      setError(err.message || 'Failed to start camera');
      onError?.(err);
      toast.error('Camera Error', { description: err.message });
    }
  };

  const stopScanning = async () => {
    try {
      if (html5QrCodeRef.current && isScanning) {
        await html5QrCodeRef.current.stop();
      }
    } catch (err) {
      console.warn('Error stopping scanner:', err);
    }
    setIsScanning(false);
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
      scanTimeoutRef.current = null;
    }
  };

  const switchCamera = async () => {
    if (cameras.length <= 1) return;

    await stopScanning();

    const currentIndex = cameras.findIndex((c) => c.id === selectedCamera);
    const nextIndex = (currentIndex + 1) % cameras.length;
    setSelectedCamera(cameras[nextIndex].id);

    // Restart scanning with new camera
    setTimeout(() => startScanning(), 500);
  };

  const toggleScanning = () => {
    if (isScanning) {
      stopScanning();
    } else {
      startScanning();
    }
  };

  if (!hasCamera) {
    return (
      <Card className={`${className}`}>
        <CardContent className="p-6 text-center">
          <CameraOff className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="font-semibold text-lg mb-2">{t("noCameraAvailable", "No Camera Available")}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {error || 'Camera access is required for barcode scanning'}
          </p>
          <Badge variant="outline" className="gap-2">
            <Smartphone className="h-3 w-3" />{t("useAndroidChrome", "Use Android Chrome for best results")}

          </Badge>
        </CardContent>
      </Card>);

  }

  return (
    <div className={`space-y-4 ${className}`} data-testid="camera-scanner">
      {/* Scanner viewport */}
      <div className="relative rounded-lg overflow-hidden bg-black">
        <div
          id="camera-scanner-reader"
          ref={scannerRef}
          className="w-full aspect-[4/3] bg-gray-900"
          style={{ minHeight: '280px' }} />
        
        
        {/* Overlay when not scanning */}
        {!isScanning &&
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900/90">
            <div className="text-center text-white">
              <Camera className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">{t("cameraScannerReady", "Camera Scanner Ready")}</p>
              <p className="text-sm text-gray-400 mb-4">{t("clickStartTo", "Click Start to begin scanning")}</p>
              <Button onClick={startScanning} className="gap-2">
                <Camera className="h-4 w-4" />{t("startCamera", "Start Camera")}

            </Button>
            </div>
          </div>
        }

        {/* Scanning indicator */}
        {isScanning &&
        <div className="absolute top-3 left-3 flex items-center gap-2">
            <Badge variant="default" className="bg-green-600 gap-1 animate-pulse">
              <span className="h-2 w-2 rounded-full bg-white animate-pulse" />{t("scanning", "Scanning")}

          </Badge>
          </div>
        }

        {/* Last scan result badge */}
        {lastScan &&
        <div className="absolute bottom-3 left-3 right-3">
            <div className="bg-green-600/90 text-white px-3 py-2 rounded-lg flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm font-mono truncate">{lastScan.text}</span>
            </div>
          </div>
        }
      </div>

      {/* Controls */}
      {showControls &&
      <div className="flex items-center justify-between gap-2">
          <div className="flex gap-2">
            <Button
            variant={isScanning ? "destructive" : "default"}
            onClick={toggleScanning}
            className="gap-2"
            data-testid="toggle-camera-btn">
            
              {isScanning ?
            <>
                  <CameraOff className="h-4 w-4" />{t("stop", "Stop")}

            </> :

            <>
                  <Camera className="h-4 w-4" />{t("start", "Start")}

            </>
            }
            </Button>
            
            {cameras.length > 1 &&
          <Button
            variant="outline"
            onClick={switchCamera}
            disabled={!isScanning}
            className="gap-2">
            
                <SwitchCamera className="h-4 w-4" />{t("switch", "Switch")}

          </Button>
          }
          </div>

          <Button
          variant="ghost"
          size="icon"
          onClick={() => setSoundEnabled(!soundEnabled)}
          title={soundEnabled ? 'Mute beep' : 'Enable beep'}>
          
            {soundEnabled ?
          <Volume2 className="h-4 w-4" /> :

          <VolumeX className="h-4 w-4" />
          }
          </Button>
        </div>
      }

      {/* Error display */}
      {error &&
      <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-center gap-2">
          <XCircle className="h-4 w-4 text-red-600" />
          <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
        </div>
      }

      {/* Camera info */}
      {selectedCamera && cameras.length > 0 &&
      <p className="text-xs text-muted-foreground text-center">{t("using", "Using:")}
        {cameras.find((c) => c.id === selectedCamera)?.label || 'Camera'}
        </p>
      }
    </div>);

};

export default CameraScanner;